import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useToast,
  Badge,
  HStack,
  VStack,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
  Alert,
  AlertIcon,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon
} from '@chakra-ui/react';
import { FiEdit, FiTrash2, FiEye, FiPlus, FiCheck, FiMove } from 'react-icons/fi';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './FAQAdmin.css';

// API base URL based on environment
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL || 'https://api.veridicus.com.br'
  : process.env.REACT_APP_LOCAL_API_URL || 'http://localhost:5000';

const FAQAdmin = () => {
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  
  // Form data for categories
  const [categoryForm, setCategoryForm] = useState({
    title: '',
    order: '',
    isActive: true
  });
  
  // Form data for questions
  const [questionForm, setQuestionForm] = useState({
    question: '',
    answer: '',
    category: '',
    order: '',
    isActive: true
  });
  
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deletingType, setDeletingType] = useState('');
  
  const toast = useToast();
  const { isOpen: isCategoryOpen, onOpen: onCategoryOpen, onClose: onCategoryClose } = useDisclosure();
  const { isOpen: isQuestionOpen, onOpen: onQuestionOpen, onClose: onQuestionClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();

  // Quill editor modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      ['link'],
      ['clean']
    ],
  };

  useEffect(() => {
    fetchCategories();
    fetchQuestions();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/faq/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar categorias:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar categorias',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async (categoryId = null) => {
    try {
      const token = localStorage.getItem('token');
      const url = categoryId 
        ? `${API_BASE_URL}/api/faq/questions?category=${categoryId}`
        : `${API_BASE_URL}/api/faq/questions`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setQuestions(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao buscar perguntas:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar perguntas',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Category Management
  const handleOpenCategoryModal = (category = null) => {
    if (category) {
      setCategoryForm({
        title: category.title,
        order: category.order,
        isActive: category.isActive
      });
      setEditingCategoryId(category._id);
    } else {
      setCategoryForm({
        title: '',
        order: categories.length,
        isActive: true
      });
      setEditingCategoryId(null);
    }
    onCategoryOpen();
  };

  const handleCloseCategoryModal = () => {
    setCategoryForm({ title: '', order: '', isActive: true });
    setEditingCategoryId(null);
    onCategoryClose();
  };

  const handleSubmitCategory = async () => {
    try {
      if (!categoryForm.title) {
        toast({
          title: 'Erro',
          description: 'O título da categoria é obrigatório',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const token = localStorage.getItem('token');
      const endpoint = editingCategoryId
        ? `${API_BASE_URL}/api/faq/categories/${editingCategoryId}`
        : `${API_BASE_URL}/api/faq/categories`;
      
      const method = editingCategoryId ? 'put' : 'post';
      
      const response = await axios[method](endpoint, categoryForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast({
          title: 'Sucesso',
          description: editingCategoryId
            ? 'Categoria atualizada com sucesso'
            : 'Categoria criada com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchCategories();
        handleCloseCategoryModal();
      }
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao salvar categoria',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Question Management
  const handleOpenQuestionModal = (question = null) => {
    if (question) {
      setQuestionForm({
        question: question.question,
        answer: question.answer,
        category: question.category._id || question.category,
        order: question.order,
        isActive: question.isActive
      });
      setEditingQuestionId(question._id);
    } else {
      setQuestionForm({
        question: '',
        answer: '',
        category: selectedCategory || (categories[0]?._id || ''),
        order: '',
        isActive: true
      });
      setEditingQuestionId(null);
    }
    onQuestionOpen();
  };

  const handleCloseQuestionModal = () => {
    setQuestionForm({ question: '', answer: '', category: '', order: '', isActive: true });
    setEditingQuestionId(null);
    onQuestionClose();
  };

  const handleSubmitQuestion = async () => {
    try {
      if (!questionForm.question || !questionForm.answer || !questionForm.category) {
        toast({
          title: 'Erro',
          description: 'Pergunta, resposta e categoria são obrigatórios',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const token = localStorage.getItem('token');
      const endpoint = editingQuestionId
        ? `${API_BASE_URL}/api/faq/questions/${editingQuestionId}`
        : `${API_BASE_URL}/api/faq/questions`;
      
      const method = editingQuestionId ? 'put' : 'post';
      
      const response = await axios[method](endpoint, questionForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast({
          title: 'Sucesso',
          description: editingQuestionId
            ? 'Pergunta atualizada com sucesso'
            : 'Pergunta criada com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchQuestions();
        handleCloseQuestionModal();
      }
    } catch (error) {
      console.error('Erro ao salvar pergunta:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao salvar pergunta',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Delete functions
  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = deletingType === 'category'
        ? `${API_BASE_URL}/api/faq/categories/${deletingId}`
        : `${API_BASE_URL}/api/faq/questions/${deletingId}`;
      
      const response = await axios.delete(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast({
          title: 'Sucesso',
          description: `${deletingType === 'category' ? 'Categoria' : 'Pergunta'} excluída com sucesso`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        if (deletingType === 'category') {
          fetchCategories();
        } else {
          fetchQuestions();
        }
        
        onDeleteClose();
        setDeletingId(null);
        setDeletingType('');
      }
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast({
        title: 'Erro',
        description: error.response?.data?.message || `Erro ao excluir ${deletingType === 'category' ? 'categoria' : 'pergunta'}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const openDeleteDialog = (id, type) => {
    setDeletingId(id);
    setDeletingType(type);
    onDeleteOpen();
  };

  // Group questions by category
  const groupedQuestions = categories.map(category => ({
    ...category,
    questions: questions.filter(q => q.category?._id === category._id || q.category === category._id)
  }));

  return (
    <Container maxW="container.xl" py={5}>
      <VStack spacing={5} align="stretch">
        <HStack justify="space-between">
          <Heading size="lg">Gerenciar Perguntas Frequentes</Heading>
          <HStack>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="blue"
              onClick={() => handleOpenCategoryModal()}
            >
              Nova Categoria
            </Button>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="green"
              onClick={() => handleOpenQuestionModal()}
            >
              Nova Pergunta
            </Button>
          </HStack>
        </HStack>

        {loading ? (
          <Box textAlign="center" py={10}>
            <Spinner size="xl" color="green.500" />
          </Box>
        ) : (
          <Tabs>
            <TabList>
              <Tab>Por Categoria</Tab>
              <Tab>Todas as Perguntas</Tab>
              <Tab>Categorias</Tab>
            </TabList>

            <TabPanels>
              {/* Tab 1: Questions by Category */}
              <TabPanel>
                {groupedQuestions.length === 0 ? (
                  <Alert status="info">
                    <AlertIcon />
                    Nenhuma categoria cadastrada. Clique em "Nova Categoria" para começar.
                  </Alert>
                ) : (
                  <Accordion allowMultiple>
                    {groupedQuestions.map(category => (
                      <AccordionItem key={category._id}>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <HStack>
                                <Text fontWeight="bold">{category.title}</Text>
                                <Badge colorScheme={category.isActive ? 'green' : 'gray'}>
                                  {category.isActive ? 'Ativa' : 'Inativa'}
                                </Badge>
                                <Badge colorScheme="blue">
                                  {category.questions.length} perguntas
                                </Badge>
                              </HStack>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <VStack align="stretch" spacing={3}>
                            {category.questions.length === 0 ? (
                              <Text color="gray.500">Nenhuma pergunta nesta categoria</Text>
                            ) : (
                              category.questions.map((question, index) => (
                                <Box
                                  key={question._id}
                                  p={3}
                                  border="1px solid"
                                  borderColor="gray.200"
                                  borderRadius="md"
                                >
                                  <HStack justify="space-between">
                                    <VStack align="start" flex="1">
                                      <Text fontWeight="bold">{index + 1}. {question.question}</Text>
                                      <Text 
                                        fontSize="sm" 
                                        color="gray.600"
                                        dangerouslySetInnerHTML={{ __html: question.answer.substring(0, 150) + '...' }}
                                      />
                                    </VStack>
                                    <HStack>
                                      <Badge colorScheme={question.isActive ? 'green' : 'gray'}>
                                        {question.isActive ? 'Ativa' : 'Inativa'}
                                      </Badge>
                                      <IconButton
                                        aria-label="Editar"
                                        icon={<FiEdit />}
                                        size="sm"
                                        colorScheme="yellow"
                                        variant="ghost"
                                        onClick={() => handleOpenQuestionModal(question)}
                                      />
                                      <IconButton
                                        aria-label="Excluir"
                                        icon={<FiTrash2 />}
                                        size="sm"
                                        colorScheme="red"
                                        variant="ghost"
                                        onClick={() => openDeleteDialog(question._id, 'question')}
                                      />
                                    </HStack>
                                  </HStack>
                                </Box>
                              ))
                            )}
                            <Button
                              size="sm"
                              leftIcon={<FiPlus />}
                              variant="outline"
                              onClick={() => {
                                setSelectedCategory(category._id);
                                handleOpenQuestionModal();
                              }}
                            >
                              Adicionar pergunta nesta categoria
                            </Button>
                          </VStack>
                        </AccordionPanel>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </TabPanel>

              {/* Tab 2: All Questions */}
              <TabPanel>
                {questions.length === 0 ? (
                  <Alert status="info">
                    <AlertIcon />
                    Nenhuma pergunta cadastrada. Clique em "Nova Pergunta" para criar a primeira.
                  </Alert>
                ) : (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Pergunta</Th>
                        <Th>Categoria</Th>
                        <Th>Status</Th>
                        <Th>Ações</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {questions.map(question => (
                        <Tr key={question._id}>
                          <Td>{question.question}</Td>
                          <Td>{question.category?.title || 'Sem categoria'}</Td>
                          <Td>
                            <Badge colorScheme={question.isActive ? 'green' : 'gray'}>
                              {question.isActive ? 'Ativa' : 'Inativa'}
                            </Badge>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <IconButton
                                aria-label="Editar"
                                icon={<FiEdit />}
                                size="sm"
                                colorScheme="yellow"
                                variant="ghost"
                                onClick={() => handleOpenQuestionModal(question)}
                              />
                              <IconButton
                                aria-label="Excluir"
                                icon={<FiTrash2 />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => openDeleteDialog(question._id, 'question')}
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </TabPanel>

              {/* Tab 3: Categories */}
              <TabPanel>
                {categories.length === 0 ? (
                  <Alert status="info">
                    <AlertIcon />
                    Nenhuma categoria cadastrada. Clique em "Nova Categoria" para criar a primeira.
                  </Alert>
                ) : (
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Título</Th>
                        <Th>Ordem</Th>
                        <Th>Status</Th>
                        <Th>Perguntas</Th>
                        <Th>Ações</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {categories.map(category => (
                        <Tr key={category._id}>
                          <Td>{category.title}</Td>
                          <Td>{category.order}</Td>
                          <Td>
                            <Badge colorScheme={category.isActive ? 'green' : 'gray'}>
                              {category.isActive ? 'Ativa' : 'Inativa'}
                            </Badge>
                          </Td>
                          <Td>
                            {questions.filter(q => q.category?._id === category._id || q.category === category._id).length}
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <IconButton
                                aria-label="Editar"
                                icon={<FiEdit />}
                                size="sm"
                                colorScheme="yellow"
                                variant="ghost"
                                onClick={() => handleOpenCategoryModal(category)}
                              />
                              <IconButton
                                aria-label="Excluir"
                                icon={<FiTrash2 />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => openDeleteDialog(category._id, 'category')}
                              />
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        )}
      </VStack>

      {/* Category Modal */}
      <Modal isOpen={isCategoryOpen} onClose={handleCloseCategoryModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingCategoryId ? 'Editar Categoria' : 'Nova Categoria'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Título</FormLabel>
                <Input
                  value={categoryForm.title}
                  onChange={(e) => setCategoryForm({ ...categoryForm, title: e.target.value })}
                  placeholder="Ex: Perguntas Gerais"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Ordem</FormLabel>
                <Input
                  type="number"
                  value={categoryForm.order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">Categoria ativa?</FormLabel>
                <Switch
                  isChecked={categoryForm.isActive}
                  onChange={(e) => setCategoryForm({ ...categoryForm, isActive: e.target.checked })}
                  colorScheme="green"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseCategoryModal}>
              Cancelar
            </Button>
            <Button colorScheme="green" onClick={handleSubmitCategory}>
              {editingCategoryId ? 'Atualizar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Question Modal */}
      <Modal isOpen={isQuestionOpen} onClose={handleCloseQuestionModal} size="xl">
        <ModalOverlay />
        <ModalContent maxW="900px">
          <ModalHeader>
            {editingQuestionId ? 'Editar Pergunta' : 'Nova Pergunta'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Pergunta</FormLabel>
                <Input
                  value={questionForm.question}
                  onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })}
                  placeholder="Digite a pergunta"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Resposta</FormLabel>
                <Box className="quill-container">
                  <ReactQuill
                    value={questionForm.answer}
                    onChange={(value) => setQuestionForm({ ...questionForm, answer: value })}
                    modules={modules}
                    theme="snow"
                    placeholder="Digite a resposta..."
                    style={{ height: '200px', marginBottom: '50px' }}
                  />
                </Box>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Categoria</FormLabel>
                <Select
                  value={questionForm.category}
                  onChange={(e) => setQuestionForm({ ...questionForm, category: e.target.value })}
                >
                  <option value="">Selecione uma categoria</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat._id}>{cat.title}</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Ordem</FormLabel>
                <Input
                  type="number"
                  value={questionForm.order}
                  onChange={(e) => setQuestionForm({ ...questionForm, order: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0">Pergunta ativa?</FormLabel>
                <Switch
                  isChecked={questionForm.isActive}
                  onChange={(e) => setQuestionForm({ ...questionForm, isActive: e.target.checked })}
                  colorScheme="green"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseQuestionModal}>
              Cancelar
            </Button>
            <Button colorScheme="green" onClick={handleSubmitQuestion}>
              {editingQuestionId ? 'Atualizar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Excluir {deletingType === 'category' ? 'Categoria' : 'Pergunta'}
            </AlertDialogHeader>
            <AlertDialogBody>
              Tem certeza que deseja excluir {deletingType === 'category' ? 'esta categoria' : 'esta pergunta'}? 
              Esta ação não pode ser desfeita.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default FAQAdmin;